import { Link } from 'react-router';
import {
  ArrowRight,
  Banknote,
  BookOpen,
  Camera,
  CalendarClock,
  Clock4,
  FileSpreadsheet,
  PenLine,
  ShieldCheck,
} from 'lucide-react';
import { APP_NAME } from '@helfr/domain';
import { NahledAplikace } from '../components/NahledAplikace.js';
import { VerejnaHlavicka } from '../components/VerejnaHlavicka.js';

/**
 * Veřejná landing page (kořen webu pro nepřihlášené) — tři bloky hodnoty se
 * SKUTEČNÝMI screenshoty aplikace. Zásada důvěry: jen fakta o tom, co systém
 * dělá — žádná vymyšlená čísla, reference ani sliby úspor.
 */

function Cta({ velky = false }: { velky?: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Link
        to="/registrace"
        className={`inline-flex items-center gap-2 rounded-lg bg-accent font-bold text-text-heading transition-colors duration-100 hover:bg-accent-hover ${
          velky ? 'min-h-13 px-6 text-lg' : 'min-h-11 px-5 text-base'
        }`}
      >
        Založte firmu zdarma
        <ArrowRight size={18} strokeWidth={2.25} aria-hidden="true" />
      </Link>
      <Link
        to="/login"
        className={`inline-flex items-center rounded-lg border border-white/30 font-semibold text-white transition-colors duration-100 hover:bg-white/10 ${
          velky ? 'min-h-13 px-6 text-lg' : 'min-h-11 px-5 text-base'
        }`}
      >
        Přihlásit se
      </Link>
    </div>
  );
}

/** Screenshot aplikace — kliknutím se zvětší (sdílená komponenta). */
const Ukazka = NahledAplikace;

function Blok({
  ikona,
  nadpis,
  podtitul,
  body,
  obrazek,
  alt,
  otoceny = false,
}: {
  ikona: React.ReactNode;
  nadpis: string;
  podtitul: string;
  body: string[];
  obrazek: string;
  alt: string;
  otoceny?: boolean;
}) {
  return (
    <section className="mx-auto grid max-w-6xl items-center gap-8 px-6 py-12 lg:grid-cols-2 lg:gap-14">
      <div className={otoceny ? 'lg:order-2' : ''}>
        <span className="mb-3 inline-flex size-11 items-center justify-center rounded-xl bg-primary-tint text-primary">
          {ikona}
        </span>
        <h2 className="text-2xl font-bold text-balance lg:text-3xl">{nadpis}</h2>
        <p className="mt-2 text-lg text-text-secondary">{podtitul}</p>
        <ul className="mt-4 flex flex-col gap-2.5">
          {body.map((b) => (
            <li key={b} className="flex items-start gap-2.5 text-base">
              <span aria-hidden="true" className="mt-1.5 size-2 shrink-0 rounded-full bg-accent" />
              {b}
            </li>
          ))}
        </ul>
      </div>
      <div className={otoceny ? 'lg:order-1' : ''}>
        <Ukazka src={obrazek} alt={alt} />
      </div>
    </section>
  );
}

export function LandingPage() {
  return (
    <main className="min-h-screen bg-bg">
      <VerejnaHlavicka />

      {/* Hero */}
      <section className="bg-primary pb-20 text-white">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-6 pt-10 lg:grid-cols-[1.05fr_1fr] lg:pt-16">
          <div>
            <h1 className="text-4xl leading-tight font-bold text-balance text-white lg:text-5xl">
              Ať se žádná vícepráce neztratí.
            </h1>
            <p className="mt-4 max-w-prose text-xl text-primary-tint">
              Helfr je stavební deník a hlídač víceprací pro menší generální dodavatele — dovede
              fotku z terénu až k zaplacené faktuře.
            </p>
            <div className="mt-8">
              <Cta velky />
            </div>
            <p className="mt-10 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs font-semibold tracking-wider uppercase">
              <span className="text-primary-tint">Nález</span>
              <span aria-hidden="true" className="text-accent">
                →
              </span>
              <span className="text-primary-tint">Změna</span>
              <span aria-hidden="true" className="text-accent">
                →
              </span>
              <span className="text-primary-tint">Dodatek</span>
              <span aria-hidden="true" className="text-accent">
                →
              </span>
              <span className="text-accent">Zaplacená faktura</span>
            </p>
          </div>
          <Ukazka
            src="/marketing/prehled.png"
            alt="Přehled zakázky v Helfru — co dnes hoří, fronta nálezů a hlídače"
            prioritni
          />
        </div>
      </section>

      {/* Tři bloky hodnoty */}
      <Blok
        ikona={<BookOpen size={22} strokeWidth={1.75} />}
        nadpis="Deník bez děr — právní jistota"
        podtitul="Stavební deník je při sporu první, co se čte. Helfr ho hlídá za vás."
        body={[
          'Mistr den nadiktuje, přidá fotky a podepíše prstem — podpis den uzamkne.',
          'Kalendář ukáže díry v deníku dřív, než je najde právník protistrany.',
          'Výzvy TDI k přejímce zakrývaných konstrukcí s termínem a fotem před zakrytím.',
          'Export celého deníku do PDF a Excelu na jedno kliknutí.',
        ]}
        obrazek="/marketing/denik.png"
        alt="Deník v Helfru — kalendář podepsaných dnů a čtečka záznamů"
      />
      <div className="bg-surface">
        <Blok
          otoceny
          ikona={<Banknote size={22} strokeWidth={1.75} />}
          nadpis="Hlídač víceprací — peníze, co neutečou"
          podtitul="Vícepráce se ztrácejí po cestě z terénu do fakturace. Helfr tu cestu hlídá celou."
          body={[
            'Nález z terénu (foto + pár slov) padá do fronty přípraváře — nic nezapadne.',
            'Změna má dvě strany: co fakturujete investorovi a co vás to stojí. Marže je vidět průběžně.',
            'Hlídače upozorní na náklad bez výnosu, práci provedenou bez podpisu i limit § 222 u veřejných zakázek.',
            'Rekapitulace pro investora odchází bez vašich nákladů a marží — zeď je v systému, ne v šabloně.',
          ]}
          obrazek="/marketing/zmena.png"
          alt="Detail změny v Helfru — dvě strany, marže a stavový průvodce"
        />
      </div>
      <Blok
        ikona={<PenLine size={22} strokeWidth={1.75} />}
        nadpis="Podpisy bez papíru"
        podtitul="Konec honění chlapů s propiskou po stavbě."
        body={[
          'Podpis rukou na obrazovce — prstem na mobilu, myší na počítači, nebo QR kódem z desktopu na telefon.',
          'Subdodavatel i TDI podepíší přes zabezpečený odkaz — bez registrace a bez instalace.',
          'Změnové listy, dodatky, zjišťovací i předávací protokoly — s podpisy rovnou v PDF.',
          'Každý podpis se ukládá s časem a IP adresou jako doklad.',
        ]}
        obrazek="/marketing/podpis.png"
        alt="Veřejná podpisová stránka Helfru — podpis změnového listu prstem"
      />

      {/* Pás dalších funkcí — fakta bez okecávání */}
      <section className="bg-primary py-12 text-white">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-xl font-bold text-white">A k tomu všechno kolem stavby:</h2>
          <ul className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                Ikona: Clock4,
                text: 'Docházka party s Excel exportem pro mzdový systém',
              },
              { Ikona: Camera, text: 'Fotogalerie stavby — všechny fotky po dnech a objektech' },
              {
                Ikona: FileSpreadsheet,
                text: 'Zjišťovací protokoly — měsíční čerpání jako podklad faktury',
              },
              {
                Ikona: CalendarClock,
                text: 'Milníky s penále — nárok na prodloužení doložený změnami',
              },
            ].map(({ Ikona, text }) => (
              <li key={text} className="flex items-start gap-3">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-white/10">
                  <Ikona size={18} strokeWidth={1.75} aria-hidden="true" />
                </span>
                <span className="text-sm text-primary-tint">{text}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/funkce"
            className="mt-6 inline-flex items-center gap-1.5 font-semibold text-accent underline-offset-2 hover:underline"
          >
            Projít všechny funkce do detailu
            <ArrowRight size={16} strokeWidth={2.25} aria-hidden="true" />
          </Link>
        </div>
      </section>

      {/* Závěrečné CTA */}
      <section className="bg-primary pb-16 text-white">
        <div className="mx-auto flex max-w-6xl flex-col items-start gap-5 border-t border-white/15 px-6 pt-12">
          <ShieldCheck size={28} strokeWidth={1.75} aria-hidden="true" className="text-accent" />
          <h2 className="max-w-2xl text-2xl font-bold text-balance text-white lg:text-3xl">
            Začněte zdarma — a nejdřív si všechno prohlédněte na ukázkové stavbě se vzorovými daty.
          </h2>
          <Cta velky />
        </div>
      </section>

      <footer className="bg-primary-pressed py-6 text-center text-sm text-primary-tint">
        {APP_NAME} — stavební deník a hlídač víceprací
      </footer>
    </main>
  );
}
